import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { message, Alert, Button, Spin, Tag, Table, Popconfirm } from 'antd';
import { useMutation } from '@apollo/client'; 
import dayjs from 'dayjs';
import { DeleteOutlined, MoreOutlined, PlusOutlined } from '@ant-design/icons';
import ModalCompletionCodes from './ModalCompletionCodes';
import { CancelSession, CreateEditILTSession, DeleteSessionModal, EditILTSessionLocalizations, SessionRegistrantsModal } from './ILTSessionModals';
import FormPublish from '../FormPublish/FormPublish';  
import useILT from '../../gql/hooks/useILT';
import useILTCodes from '../../gql/hooks/useILTCodes';
import useDeleteCompletionCode from '../../gql/hooks/useDeleteCompletionCode';
import PUBLISH_COURSE_MUTATION from '../../gql/mutations/PUBLISH_COURSE_MUTATION';
import UNPUBLISH_COURSE_MUTATION from '../../gql/mutations/UNPUBLISH_COURSE_MUTATION';
import './styles.less';
import { Dropdown } from 'antd';
import { Tooltip } from 'antd';
import { getFormattedDateTimeRange } from '../../utils';


const TabILT = forwardRef( ( { form, isSavingCallback, reloadCourseCallback }, ref ) => {
    const formPublishRef = useRef( useRef );
    const [ isSaving, setIsSaving ] = useState( false );   
    const [ addConfirmModalVisble, setAddConfirmModalVisble ] = useState( false );
    const { loadILT, iltData, iltError, iltLoading } = useILT();
    const { loadCodes, codes, codesError, codesLoading } = useILTCodes();
    const [ sessions, setSessions ] = useState( [] );
    const [ completionCodes, setCompletionCodes ] = useState( [] );

    const { deleteCompletionCode } = useDeleteCompletionCode();
    
    const [ iltID, setIltId ] = useState();
    const [ publishCourse ] = useMutation( PUBLISH_COURSE_MUTATION );
    const [ unpublishCourse ] = useMutation( UNPUBLISH_COURSE_MUTATION );
    const [ showPublishRequirements, setShowPublishRequirements ] = useState( false );
    const [ showDeleteCodeMessage, setShowDeleteCodeMessage ] = useState( false );

    useImperativeHandle( ref, () => ( {
        isModified() {
            return false;
        },
        showSaveConfirm() {
        }
    } ) );

    function handleAddEditIltCallback() {
        fetchILT();
        loadCodes( iltID );
    }
    
    const fetchILT = () => {
        const id = form.getFieldValue( 'iltID' );

        if ( id ) {
            setIltId( id );
            loadILT( id );
        }
    };

    useEffect( () => {
        fetchILT();
    }, [] );

    useEffect( () => {
        if ( iltID ) {
            loadCodes( iltID );
        }
    }, [ iltID ] );

    useEffect( () => {
        if ( codes ) {
            setCompletionCodes( codes.sort( ( a, b ) => 
                dayjs( b.createdAt ).isBefore( dayjs( a.createdAt ) ) ? -1 : 1 ) );
        }
    }, [ codes ] );

    useEffect( () => {
        if ( !iltData || !iltData.sessions ) {
            setSessions( [] );
            return;
        }

        const result = iltData.sessions.map( i => ( {
            ...i,
            name: ( i.locales.find( l => l.locale == 'en-US' ) ?? i.locales[ 0 ] ).name,
            key: i.id,
            _dataObject: i,
        } ) ).sort( ( a, b ) => 
            dayjs( a.sessionStartAt ).isBefore( dayjs( b.sessionStartAt ) ) ? -1 : 1 );

        setSessions( result );
    }, [ iltData ] );

    const handleCanPublishCallback = () => {   
        const hasSessions = sessions && sessions.length > 0;
        const hasCodes = completionCodes && completionCodes.length > 0;
        
        setShowPublishRequirements( !( hasSessions && hasCodes ) );
        return ( hasSessions && hasCodes );
    };

    const handleAddCode = () => {
        setAddConfirmModalVisble( true ); 
    };

    const handleAddCodeFinished = async ( code ) => {
        if ( code ) {
            setAddConfirmModalVisble( false ); 
            setCompletionCodes( [ ...completionCodes, code ].sort( ( a, b ) => 
                dayjs( b.createdAt ).isBefore( dayjs( a.createdAt ) ) ? -1 : 1 ) );
        } else {
            setAddConfirmModalVisble( false ); 
        }
    };
    
    const handleDeleteCode = async ( e ) => {
        setShowDeleteCodeMessage( false );
        const result = await deleteCompletionCode( e.id, e.instructorLedTrainingId );
        
        if ( result ) { 
            setCompletionCodes( completionCodes.filter( code => code.id !== e.id ) );
        } else {
            setShowDeleteCodeMessage( true );
        }
    };

    const handleIsPublishedSave = async ( value ) => {
        setIsSaving( true );  
        
        if ( value ) {
            try {
                const response = await publishCourse( {
                    variables: { 
                        courseID: form.getFieldValue( 'id' )
                    }
                } );

                if ( response.data.result.success ) {
                    form.setFieldsValue( { published: form.getFieldValue( 'publishedModified' ) } );
                    form.setFieldsValue( { hasRevision: true } );  
                    formPublishRef.current.reset();
                    reloadCourseCallback();
                }

            } catch ( e ) {
                console.error( e ); 
                message.error( 'Error saving.' ); 
            }   
        } else {
            try {
                const response = await unpublishCourse( {
                    variables: { 
                        courseID: form.getFieldValue( 'id' )
                    }
                } );
    
                if ( response.data.result.success ) {
                    form.setFieldsValue( { published: form.getFieldValue( 'publishedModified' ) } );
                    reloadCourseCallback();
                } else {
                    message.error( 'Error saving.' ); 
                }
            } catch ( e ) {
                console.error( e );
                message.error( 'Error saving.' ); 
            }
        }

        setIsSaving( false ); 
    };

    const tableColumns = [
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sortKey: 'name',
            fixed: 'left',  
            className: 'name-column' 
        },
        {
            dataIndex: 'timezone',
            key: 'timezone', 
            title: 'Timezone',
            sortKey: 'timezone',
            render: ( tz ) => `${ tz.replaceAll( '/', ' / ' ).replaceAll( '_', ' ' ) } (UTC${ dayjs().tz( tz ).format( 'Z' ) })`,
            fixed: 'left',
        },
        {
            dataIndex: 'maxSeats',
            key: 'maxSeats', 
            title: 'Seats',
            sortKey: 'maxSeats',
            fixed: 'left',
            render: ( i, item ) => `${ item.filledSeats } of ${ i } Filled`
        },
        {
            key: 'sessionWindow',
            title: 'Session Time',
            render: ( _, { sessionStartAt, sessionEndAt, timezone } ) => getFormattedDateTimeRange( sessionStartAt, sessionEndAt, timezone )
        },
        {
            dataIndex: 'meetingUrl',
            key: 'meetingUrl', 
            title: 'Type',
            fixed: 'left',
            render: ( i ) => i ? <Tag color="blue">Virtual</Tag> : <Tag color="purple">Live</Tag>
        },
        {
            dataIndex: 'completionCount',
            key: 'completions',
            title: 'Completions'
        },
        {
            dataIndex: 'status',
            key: 'status', 
            title: 'Status',
            sortKey: 'status',
            fixed: 'left',
            render: ( i ) => i == 'ACTIVE' ? <Tag color="green">Active</Tag> : <Tag color='gray'>Canceled</Tag>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            align: 'center',
            width: '100px',
            render: ( item ) => {
                return <Dropdown
                    trigger={ [ 'click' ] }
                    menu={ { 
                        className: 'session-action-dropdown',
                        items: [
                            {
                                key: 'edit-localizations',
                                label: <EditILTSessionLocalizations finishedCallback={ fetchILT } iltID={ iltID } session={ item } />
                            }, {
                                key: 'edit-session',
                                label: <CreateEditILTSession finishedCallback={ handleAddEditIltCallback } iltID={ iltID } editSession={ item } />
                            }, {
                                key: 'cancel-session',
                                label: <CancelSession finishedCallback={ fetchILT } iltID={ iltID } session={ item } />
                            }, {
                                key: 'view-registrants',
                                label: <SessionRegistrantsModal iltID={ iltID } session={ item } />
                            }, {
                                key: 'delete-session',
                                label: <DeleteSessionModal session={ item } finishedCallback={ handleAddEditIltCallback } iltID={ iltID } />
                            }
                    ] } }
                    >
                    <MoreOutlined className='clickable' />
                </Dropdown>;
            } 
            
        }
    ];


    const codesTableColumns = [
        {
            dataIndex: 'code',
            key: 'code', 
            title: 'Code',
            sortKey: 'code',
            fixed: 'left',  
            className: 'code-column' 
        },
        {
            dataIndex: 'usedBySessionsCount',
            key: 'usedBySessionsCount', 
            title: 'Number of Sessions Using This Code',
            sortKey: 'usedBySessionsCount',
            render: count => count ?? 0,
        },
        {
            dataIndex: 'sessionCompletionsCount',
            key: 'sessionCompletionsCount', 
            title: 'Total Completed Sessions',
            sortKey: 'sessionCompletionsCount',
            render: count => count ?? 0,
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                const disabled = item.usedBySessionsCount > 0 || completionCodes.length == 1;
                return (
                    <Popconfirm
                        title="Are you sure to delete this completion code?"
                        onConfirm={ () => handleDeleteCode( item ) } 
                        okText="Yes"
                        disabled={ disabled }
                        cancelText="No"
                    >
                        <Tooltip
                            title={ completionCodes.length == 1 ? 'The course must have at least one completion code.' :
                                item.usedBySessionsCount > 0 ? 'Can not delete a code used by sessions.' : ''
                            }
                            disabled={ disabled }
                            >
                            <Button 
                                size="small"
                                disabled={ disabled }
                                type="link"
                                style={ { color: disabled ? '#888' : '#ff4d4f' } }
                                icon={ <DeleteOutlined /> } 
                            />
                        </Tooltip>
                    </Popconfirm>
                );
            } 
            
        }
    ];

    return (
        <div className="tab-ilt tab-main-columns">

            { iltError || codesError && 
                <Alert 
                    type="error"
                    message="Error"
                    description={
                        <>
                            <p>An unknown error occurred. Please try again later.</p>
                            <Button
                                onClick={ () => handleAddEditIltCallback() }
                            >
                                Reload
                            </Button>
                        </>
                    }
                />
            }

            { !iltError &&
                <>
                    <div className="left">
                        <Spin spinning={ iltLoading || isSaving }>

                            { showDeleteCodeMessage &&
                                <Alert 
                                    type="error"
                                    message="Error"
                                    style={ { marginBottom: '24px' } }
                                    closable
                                    onClose={ () => setShowDeleteCodeMessage( false ) }
                                    description="This code cannot be deleted because it is being used by a session."
                                />
                            }
                            
                            <FormPublish 
                                form={ form }
                                ref={ formPublishRef } 
                                hideHelp={ true }
                                saveCallback={ handleIsPublishedSave } 
                                canPublishCallback={ handleCanPublishCallback }
                            />

                            { showPublishRequirements && 
                                <p className="ant-form-item-explain-error">
                                    You must add at least one session and one completion code before publishing.
                                </p>
                            }

                            <div className="form-item">
                                <div className="flex-container-buttons">
                                    <div className="left">
                                        <h3 
                                            htmlFor="sessions"   
                                            title="Sessions"
                                        >
                                            Sessions
                                        </h3>
                                    </div>
                                    <div className="right">
                                    
                                        <CreateEditILTSession iltID={ iltID } finishedCallback={ handleAddEditIltCallback } />
                                    </div>
                                </div>

                                <Table
                                    style={ { marginTop: '10px' } }
                                    size="small"
                                    columns={ tableColumns }
                                    dataSource={ sessions }  
                                    scroll={ { x: 'max-content' } }
                                    bordered={ false }
                                    pagination={ { 
                                        defaultPageSize: 5,
                                        showSizeChanger: false,
                                        hideOnSinglePage: true
                                    } }
                                    className="session-table"
                                />

                            </div>
                            
                            <div 
                                className="form-item"
                                style={ { marginTop: '26px' } }
                            >
                                <div className="flex-container-buttons">
                                   
                                    <div className="left">
                                        <h3 
                                            htmlFor="completion-codes"   
                                            title="Completion Codes"
                                        >
                                            Completion Codes
                                        </h3>
                                         
                                    
                                    </div>
                                    <div className="right">
                                        <Button
                                            primary
                                            icon={ <PlusOutlined /> }
                                            onClick={ handleAddCode }
                                            >Add</Button>
                                    </div>
                                </div>
                            
                                { !codesError && !codesLoading && 
                                    <Table
                                        style={ { marginTop: '10px' } }
                                        size="small"
                                        columns={ codesTableColumns }
                                        dataSource={ completionCodes }  
                                        scroll={ { x: 'max-content' } }
                                        bordered={ false }
                                        pagination={ { 
                                            defaultPageSize: 5,
                                            showSizeChanger: false 
                                        } }
                                        className="codes-table"
                                    />
                                }
                            </div>
                        </Spin>
                    </div>

                    { addConfirmModalVisble && 
                        <ModalCompletionCodes 
                            iltID={ iltID }
                            finishedCallback={ handleAddCodeFinished } 
                        />
                    } 
                </>
            }
        </div>
    );
} );



export default TabILT;
